import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ClipboardX,
  TrendingDown,
  TrendingUp,
  Download,
  Info,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { ExportDialog } from '../../../components/exports/ExportDialog';
import { adminService } from '../../../services/adminService';
import { toast } from 'sonner';
import { LocationLine } from '../../../components/LocationLine';

const PAGE_SIZE = 25;

const FilterSelect = ({ label, value, onChange, options, testId }) => (
  <div className="min-w-[160px]">
    <Label className="text-xs text-gray-500">{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 mt-1" data-testid={testId}>
        <SelectValue placeholder={`All ${label.toLowerCase()}s`} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All {label.toLowerCase()}s</SelectItem>
        {(options || []).map((o) => (
          <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

export default function PickupDiscrepancies() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [exportOpen, setExportOpen] = useState(false);

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [clientId, setClientId] = useState('all');
  const [productId, setProductId] = useState('all');
  const [driverId, setDriverId] = useState('all');
  const [bakerId, setBakerId] = useState('all');

  const filterParams = useMemo(() => {
    const p = {};
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    if (clientId !== 'all') p.client_id = clientId;
    if (productId !== 'all') p.product_id = productId;
    if (driverId !== 'all') p.driver_id = driverId;
    if (bakerId !== 'all') p.baker_id = bakerId;
    return p;
  }, [dateFrom, dateTo, clientId, productId, driverId, bakerId]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryData, listData] = await Promise.all([
        adminService.getPickupDiscrepanciesSummary(filterParams),
        adminService.getPickupDiscrepancies({ ...filterParams, page, page_size: PAGE_SIZE }),
      ]);
      setSummary(summaryData);
      setRows(listData.items || []);
      setTotal(listData.total || 0);
    } catch (error) {
      toast.error('Failed to load pickup discrepancies');
    } finally {
      setLoading(false);
    }
  }, [filterParams, page]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { setPage(1); }, [filterParams]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const options = summary?.filter_options || {};

  return (
    <div className="space-y-6" data-testid="pickup-discrepancies-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/reports')} data-testid="back-to-reports-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold text-gray-900">Pickup Discrepancies</h2>
          <p className="text-sm text-gray-500">Every quantity change recorded by drivers at pickup — spot packing problems and coach bakers.</p>
        </div>
        <Button variant="outline" onClick={() => setExportOpen(true)} data-testid="export-discrepancies-btn">
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 flex flex-wrap gap-3 items-end" data-testid="discrepancy-filters">
          <div>
            <Label className="text-xs text-gray-500">From</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 mt-1 w-40" data-testid="filter-date-from" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">To</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 mt-1 w-40" data-testid="filter-date-to" />
          </div>
          <FilterSelect label="Client" value={clientId} onChange={setClientId} options={options.clients} testId="filter-client" />
          <FilterSelect label="Product" value={productId} onChange={setProductId} options={options.products} testId="filter-product" />
          <FilterSelect label="Driver" value={driverId} onChange={setDriverId} options={options.drivers} testId="filter-driver" />
          <FilterSelect label="Baker" value={bakerId} onChange={setBakerId} options={options.bakers} testId="filter-baker" />
        </CardContent>
      </Card>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card data-testid="summary-changed-lines">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-amber-100 text-amber-700"><ClipboardX className="h-5 w-5" /></div>
            <div>
              <p className="text-2xl font-bold">{summary?.total_changed_lines ?? '—'}</p>
              <p className="text-xs text-gray-500">Changed lines</p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="summary-units-short">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-red-100 text-red-700"><TrendingDown className="h-5 w-5" /></div>
            <div>
              <p className="text-2xl font-bold">{summary?.units_short ?? '—'}</p>
              <p className="text-xs text-gray-500">Units short</p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="summary-units-over">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-purple-100 text-purple-700"><TrendingUp className="h-5 w-5" /></div>
            <div>
              <p className="text-2xl font-bold">{summary?.units_over ?? '—'}</p>
              <p className="text-xs text-gray-500">Units over (GR only)</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top products / bakers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid="top-products-card">
          <CardHeader className="pb-2"><CardTitle className="text-base">Top products by discrepancy</CardTitle></CardHeader>
          <CardContent>
            {(summary?.top_products || []).length === 0 ? (
              <p className="text-sm text-gray-400">No discrepancies for these filters.</p>
            ) : (
              <div className="space-y-2">
                {summary.top_products.map((p) => (
                  <div key={p.product_id || p.product_name} className="flex justify-between text-sm">
                    <span className="truncate">{p.product_name}</span>
                    <span className="font-medium text-gray-700">{p.total_units} units · {p.count} lines</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        <Card data-testid="top-bakers-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Top bakers by attributed discrepancies</CardTitle>
            <p className="text-xs text-gray-400 flex items-center gap-1"><Info className="h-3 w-3" /> Attribution based on production completion records</p>
          </CardHeader>
          <CardContent>
            {(summary?.top_bakers || []).length === 0 ? (
              <p className="text-sm text-gray-400">No attributed discrepancies for these filters.</p>
            ) : (
              <div className="space-y-2">
                {summary.top_bakers.map((b) => (
                  <div key={b.baker_id} className="flex justify-between text-sm">
                    <span className="truncate">{b.baker_name || b.baker_id}</span>
                    <span className="font-medium text-gray-700">{b.total_units} units · {b.count} lines</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="discrepancies-table">
            <thead className="bg-gray-50">
              <tr className="text-xs text-gray-500 uppercase">
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Order</th>
                <th className="text-left p-3">Client</th>
                <th className="text-left p-3">Location</th>
                <th className="text-left p-3">Product</th>
                <th className="text-center p-3">Ordered</th>
                <th className="text-center p-3">Delivered</th>
                <th className="text-center p-3">Delta</th>
                <th className="text-left p-3">Reason</th>
                <th className="text-left p-3">Driver</th>
                <th className="text-left p-3">Baker</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={11} className="p-8 text-center text-gray-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={11} className="p-8 text-center text-gray-400">No discrepancies found</td></tr>
              ) : rows.map((row) => (
                <tr key={row.id} className="border-t" data-testid={`discrepancy-row-${row.id}`}>
                  <td className="p-3 whitespace-nowrap">{row.delivery_date}</td>
                  <td className="p-3 whitespace-nowrap font-medium">{row.order_number}</td>
                  <td className="p-3">{row.company_name}</td>
                  <td className="p-3">
                    <LocationLine
                      name={row.location_name}
                      source={row.location_name_source}
                      clientName={row.company_name}
                      testId={`discrepancy-location-${row.id}`}
                    />
                  </td>
                  <td className="p-3">{row.product_name}</td>
                  <td className="p-3 text-center">{row.ordered_qty}</td>
                  <td className="p-3 text-center font-medium">{row.delivered_qty}</td>
                  <td className="p-3 text-center">
                    <Badge className={row.delta < 0 ? 'bg-red-100 text-red-700' : 'bg-purple-100 text-purple-700'}>
                      {row.delta > 0 ? `+${row.delta}` : row.delta}
                    </Badge>
                  </td>
                  <td className="p-3 max-w-[220px]"><span className="line-clamp-2">{row.reason}</span></td>
                  <td className="p-3 whitespace-nowrap">{row.driver_name}</td>
                  <td className="p-3 whitespace-nowrap">{row.baker_name || <span className="text-gray-400">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between text-sm text-gray-500">
        <span data-testid="discrepancies-total">{total} changed line{total === 1 ? '' : 's'}</span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="prev-page-btn">Previous</Button>
          <span>Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="next-page-btn">Next</Button>
        </div>
      </div>

      <ExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        entity="pickup-discrepancies"
        title="Export Pickup Discrepancies"
        description="Every reconciled quantity change with driver and baker attribution."
        supportsDateRange
        defaultPreset="30d"
        extraFilters={{
          ...(clientId !== 'all' ? { client_id: clientId } : {}),
          ...(driverId !== 'all' ? { driver_id: driverId } : {}),
          ...(bakerId !== 'all' ? { baker_id: bakerId } : {}),
        }}
      />
    </div>
  );
}
